import { IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { SponsoredDisplayBidRecommendations } from "@/lib/campaign-creation-api";
import { EmptyCandidates } from "./CandidateWorkspace";
import { type BidLimits, currencyLabel, formatMoney, targetBidWithinLimits } from "./money";
import type { AnyTargetRow, TargetingMode } from "./sponsored-display-draft";

export function SelectedTargets({
  rows,
  bids,
  currency,
  bidLimits,
  mode,
  onRowsChange,
}: {
  rows: AnyTargetRow[];
  bids?: SponsoredDisplayBidRecommendations;
  currency: string;
  bidLimits: BidLimits;
  mode: TargetingMode;
  onRowsChange: (rows: AnyTargetRow[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <section
      className="flex min-h-0 min-w-0 flex-col"
      aria-label={t("sd.targeting.added", { count: rows.length })}
      data-slot="sd-selected-targets"
    >
      <div
        className="flex h-14 shrink-0 items-center justify-between border-b border-line px-4 py-3"
        data-slot="sd-targeting-selected-header"
      >
        <h3 className="font-medium">{t("sd.targeting.added", { count: rows.length })}</h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={rows.length === 0}
          onClick={() => onRowsChange([])}
        >
          <IconTrash /> {t("sd.targeting.removeAll")}
        </Button>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_150px_150px_40px] gap-3 border-b border-line bg-subtle/40 px-4 py-2 text-xs font-medium text-muted-foreground">
        <span>{t("sd.targeting.productsAudiences")}</span>
        <span>{t("sd.targeting.bid")}</span>
        <span>{t("sd.targeting.suggestedBidColumn")}</span>
        <span />
      </div>
      <ScrollArea className="min-h-0 flex-1" data-slot="sd-targeting-selected-scroll" fill>
        {rows.length === 0 ? (
          <div className="flex min-h-full items-center justify-center">
            <EmptyCandidates />
          </div>
        ) : (
          <div className="divide-y divide-line">
            {rows.map((row, index) => {
              const recommendation = bids?.recommendations.find(
                (candidate) => candidate.index === index,
              );
              return (
                <div
                  className="grid grid-cols-[minmax(0,1fr)_150px_150px_40px] items-center gap-3 px-4 py-3"
                  key={row.id}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{row.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {mode === "audience"
                        ? t("sd.targeting.audience")
                        : t("sd.targeting.contextual")}
                    </p>
                  </div>
                  <Field
                    data-invalid={!targetBidWithinLimits(row.target.bid, bidLimits)}
                    data-slot="sd-targeting-row-bid-field"
                  >
                    <FieldLabel className="sr-only">
                      {t("sd.targeting.bidFor", { target: row.label })}
                    </FieldLabel>
                    <InputGroup>
                      <InputGroupAddon>{currencyLabel(currency)}</InputGroupAddon>
                      <InputGroupInput
                        aria-invalid={!targetBidWithinLimits(row.target.bid, bidLimits)}
                        aria-label={t("sd.targeting.bidFor", { target: row.label })}
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        type="number"
                        value={row.target.bid ?? ""}
                        onChange={(event) => {
                          const amount = Number(event.target.value);
                          onRowsChange(
                            rows.map((candidate) =>
                              candidate.id === row.id
                                ? {
                                    ...candidate,
                                    target: {
                                      ...candidate.target,
                                      bid: Number.isFinite(amount) && amount > 0 ? amount : null,
                                    },
                                  }
                                : candidate,
                            ),
                          );
                        }}
                      />
                    </InputGroup>
                  </Field>
                  <div className="text-xs text-muted-foreground">
                    <span className="block">{t("sd.targeting.suggestedBid")}</span>
                    {recommendation?.state === "available" ? (
                      <button
                        className="font-mono text-sm font-medium text-primary"
                        type="button"
                        onClick={() =>
                          onRowsChange(
                            rows.map((candidate) =>
                              candidate.id === row.id
                                ? {
                                    ...candidate,
                                    target: {
                                      ...candidate.target,
                                      bid: recommendation.recommended,
                                    },
                                  }
                                : candidate,
                            ),
                          )
                        }
                      >
                        {formatMoney(recommendation.recommended, currency)}
                      </button>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("sd.targeting.remove", { target: row.label })}
                    onClick={() =>
                      onRowsChange(rows.filter((candidate) => candidate.id !== row.id))
                    }
                  >
                    <IconTrash />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </section>
  );
}
